import { Component, createRef, forwardRef } from 'react';
import { Disableable, handleRefSplit, Size } from '@optimus-web/common';
import { ContainerElement, InputElement, PlaceholderElement, SizerElement } from './style';

const CURSOR_WIDTH = 2;

const copyStyles = (styles: CSSStyleDeclaration, node: HTMLDivElement) => {
    node.style.fontSize = styles.fontSize;
    node.style.fontFamily = styles.fontFamily;
    node.style.fontWeight = styles.fontWeight;
    node.style.fontStyle = styles.fontStyle;
    node.style.letterSpacing = styles.letterSpacing;
    node.style.textTransform = styles.textTransform;
};

export interface AutosizeInputProps extends Disableable {
    value: string;
    forwardedRef?: React.Ref<HTMLInputElement>;
    placeholder?: React.ReactNode;
    readOnly?: boolean;
    onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
    onKeyDown?(event: React.KeyboardEvent<HTMLElement>): void;
    onBlur?(event: React.FocusEvent<HTMLElement>): void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    inputSize?: Size;
}

interface AutosizeInputState {
    inputWidth: number;
}

class AutosizeInput extends Component<AutosizeInputProps, AutosizeInputState> {
    readonly state: AutosizeInputState = {
        inputWidth: CURSOR_WIDTH,
    };

    private inputElementRef = createRef<HTMLInputElement>();
    private sizerElementRef = createRef<HTMLDivElement>();

    componentDidMount() {
        this.copyInputStyle();
        this.updateInputWidth();
    }

    componentDidUpdate() {
        this.updateInputWidth();
    }

    render() {
        const { value, placeholder, forwardedRef, inputSize, disabled, ...other } = this.props;
        const { inputWidth } = this.state;

        const displayedValue = value || placeholder;

        const inputProps = {
            ...other,
            ref: handleRefSplit({ ownRef: this.inputElementRef, forwardedRef }),
            value,
            style: {
                width: inputWidth,
            },
            inputSize,
        };

        return (
            <ContainerElement>
                <InputElement disabled={disabled} {...inputProps} />
                {this.shouldShowPlaceholder() && (
                    <PlaceholderElement aria-hidden inputSize={inputSize} disabled={disabled}>
                        {placeholder}
                    </PlaceholderElement>
                )}
                <SizerElement ref={this.sizerElementRef} aria-hidden>
                    {displayedValue}
                </SizerElement>
            </ContainerElement>
        );
    }

    private copyInputStyle() {
        const { current: originElement } = this.inputElementRef;
        const { current: sizerElement } = this.sizerElementRef;

        if (originElement && sizerElement) {
            const styles = window.getComputedStyle(originElement);
            copyStyles(styles, sizerElement);
        }
    }

    private updateInputWidth() {
        const { current: sizerElement } = this.sizerElementRef;

        if (sizerElement) {
            const inputWidth = sizerElement.scrollWidth + CURSOR_WIDTH;
            if (this.state.inputWidth !== inputWidth) {
                this.setState({ inputWidth });
            }
        }
    }

    private readonly shouldShowPlaceholder = () =>
        this.props.placeholder && (!this.props.value || this.props.value === '');
}

export default forwardRef<HTMLInputElement, AutosizeInputProps>((props, ref) => (
    <AutosizeInput {...props} forwardedRef={ref} />
));
